/**
 * DashboardHero — the social-hero landing for /app when no recipe is open
 * (nav/dashboard redesign, Increment 2).
 *
 * Structure, top to bottom (approved mockup — dashboard-photos):
 *   1. Greeting + a Geist-Mono status line, both derived ENTIRELY from data the
 *      /app route already loads (`recipes`). No new fetch, no new endpoint.
 *   2. A photo-forward "From friends" strip: a horizontally-scrollable row of
 *      recent friend activity, composing the existing FeedItemCard with a photo
 *      banner (feature/cook photo when present, no banner when absent — photos
 *      are enrichment, not structure). Degrades to a calm doorway card when no
 *      friend activity is passed in.
 *   3. A "Resume" row for the most-recently-touched recipe, with its feature
 *      photo (or emoji) thumbnail.
 *   The library grid (LibraryView) renders unchanged BELOW this hero.
 *
 * Data contract (deliberate, per the no-new-backend constraint): loading the
 * friends lane is a PARENT concern. The /app route (nav-shell R2) loads it
 * lazily off the landing via loadFriendStrip → loadFeedLane("friends") — the
 * same feed client /app/feed uses, behind a dashboard-local freshness cache that
 * leaves the /app/feed surface untouched — and passes the result as `friendItems`.
 * When `friendItems` is empty (load in flight, empty result, or a degraded
 * failure) the strip shows its calm doorway to the feed — it never asserts "you
 * have no friends" (that would be a lie for an active user whose feed simply is
 * not loaded yet). This component neither fetches nor invents an endpoint.
 *
 * Design: design.md §2 — one accent (Ember), reserved for signal (here: the
 *   draft count + the Resume "Open" affordance's focus ring only); hairlines,
 *   never shadow; Geist Mono for DATA only (status line, timestamps, counts);
 *   tabular-nums on numerics; 200 ms quiet motion; no decoration.
 *   Section labels: Geist Mono 11px / 500 / 0.14em / uppercase / --muted.
 * Voice: branding.md §5 — quiet, no exclamations, no banned words; §8 — Recipe,
 *   Variation, Cook, Draft, go-to.
 * Rule 7 — mobile (375) + tablet (768) + desktop (≥1024) from first commit: the
 *   friends strip scrolls horizontally on narrow; the greeting row stacks.
 */

import { useEffect, useState } from "react";
import type { Recipe } from "~/features/explorer/types";
import type { FeedItem } from "~/features/feed/api";
import {
  loadNodHandles as loadNodHandlesApi,
  toggleNod as toggleNodApi,
} from "~/features/feed/api";
import { FeedItemCard } from "~/features/feed/FeedItemCard";
import { injectFeedStyles } from "~/features/feed/styles";
import { RecipeCard } from "~/features/library/RecipeCard";
import { useMediaUrl } from "~/features/media/useMediaUrl";
import { formatShortRelative } from "~/features/groups/time";
import {
  deriveDashboardStatus,
  deriveResumeRecipe,
  recipeRecency,
  greetingForHour,
} from "./derive";

const FALLBACK_EMOJI = "🍳";

// ─── CSS injection (matches the per-component <style> idiom, e.g. RecipeCard) ──

const DASHBOARD_CSS = `
.proof-dash {
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
  padding: 16px 12px 4px;
  display: flex;
  flex-direction: column;
}

/* ── Header (greeting/status left, new-recipe CTA right) ────────────────── */
.proof-dash__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
/* min-width:0 lets the greeting column shrink/wrap; the button never does. */
.proof-dash__header-text {
  min-width: 0;
}
/* The ONE reserved accent on the header — the primary create CTA (design.md §2:
   accent for primary CTA / focus rings only). Warm ink on the accent fill
   (--on-accent, fallback --bg — never literal #fff, design.md §4). Pill matches
   the header-CTA idiom; label never wraps or shrinks (Rule 7, 375px floor). */
.proof-dash__new-recipe {
  flex-shrink: 0;
  white-space: nowrap;
  color: var(--on-accent, var(--bg, #fdfcfa));
  background: var(--accent, #c23d12);
  border: 1px solid var(--accent, #c23d12);
  height: 34px;
  padding: 0 14px;
  border-radius: var(--radius-pill, 999px);
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 200ms ease;
}
.proof-dash__new-recipe:hover {
  background: color-mix(in srgb, var(--text, #1a1714) 8%, var(--accent, #c23d12));
}
.proof-dash__new-recipe:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
}
/* Clear the coarse-pointer tap-target floor (Rule 7) — matches the Library
   "New recipe" sibling. min-height wins over the 34px height on touch. */
@media (pointer: coarse) {
  .proof-dash__new-recipe {
    min-height: 44px;
  }
}

/* ── Greeting + status ──────────────────────────────────────────────────── */
.proof-dash__greet-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.proof-dash__greet {
  font-size: 22px;
  font-weight: 620;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: var(--text, #1a1714);
  text-wrap: balance;
}
.proof-dash__date {
  font-family: var(--font-mono, 'Geist Mono', ui-monospace, monospace);
  font-size: 10.5px;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--faint, #766d60);
  font-variant-numeric: tabular-nums;
}
.proof-dash__status {
  margin-top: 10px;
  font-family: var(--font-mono, 'Geist Mono', ui-monospace, monospace);
  font-size: 12px;
  color: var(--muted, #71685b);
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
}
.proof-dash__status b { color: var(--text, #1a1714); font-weight: 500; }
/* Draft count is DATA, not a signal surface — reads in ink, not the reserved
   accent (design.md §2: accent is for go-to / current-variation / primary CTA /
   focus rings only). Kept bold via the <b> inside. */
.proof-dash__status .proof-dash__status-hot { color: var(--text, #1a1714); }
.proof-dash__status .proof-dash__sep { color: var(--faint, #766d60); }

.proof-dash__hr {
  height: 1px;
  background: var(--line, #dcd7cc);
  border: 0;
  margin: 16px 0;
  width: 100%;
}

/* ── Section label ──────────────────────────────────────────────────────── */
.proof-dash__seclabel {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  margin: 0 0 11px;
}
.proof-dash__slabel {
  font-family: var(--font-mono, 'Geist Mono', ui-monospace, monospace);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted, #71685b);
}
.proof-dash__seclink {
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
  font-size: 11.5px;
  /* A section nav link is not a primary CTA — it reads muted, never in the
     reserved accent (design.md §2). Accent stays on :focus-visible only, and on
     the Resume "Open" affordance, which is the one persistent accent on-screen. */
  color: var(--muted, #71685b);
  background: transparent;
  border: 0;
  padding: 4px 2px;
  min-height: 32px;
  cursor: pointer;
  border-radius: var(--radius-btn, 8px);
}
.proof-dash__seclink:hover { text-decoration: underline; }
.proof-dash__seclink:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
}

/* ── Friends strip (horizontal scroll — zero vertical cost) ─────────────── */
.proof-dash__strip {
  display: flex;
  gap: 11px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  /* bleed to the shell edge so cards can scroll under the padding */
  margin: 0 -12px;
  padding-left: 12px;
  padding-right: 12px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.proof-dash__strip::-webkit-scrollbar { height: 0; }

.proof-dash__friend {
  min-width: 216px;
  max-width: 216px;
  scroll-snap-align: start;
  border: 1px solid var(--line, #dcd7cc);
  border-radius: var(--radius-card, 12px);
  background: var(--surface, #fdfcfa);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.proof-dash__friend-banner {
  position: relative;
  width: 100%;
  overflow: hidden;
  flex: none;
}
/* Photos are enrichment, not structure: the banner reserves NO space and shows
   NO gradient placeholder until the image has actually decoded (data-loaded).
   A photo-less or slow card collapses to the plain composed FeedItemCard — never
   a broken-looking empty tile. The <img> still loads while clipped to 0 height,
   so onLoad fires and the banner expands to its 16:9 box. */
.proof-dash__friend-banner:not([data-loaded="true"]) { height: 0; }
.proof-dash__friend-banner[data-loaded="true"] { aspect-ratio: 16 / 9; }
.proof-dash__friend-banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.proof-dash__friend-body { padding: 4px 4px 2px; }

/* The composed FeedItemCard drops its own border inside the friend card (the
   friend card supplies the border + banner); trim its default article chrome. */
.proof-dash__friend-body .proof-feed-card {
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 9px 9px 11px;
}

/* Calm doorway (no friend activity loaded here — never asserts emptiness). */
.proof-dash__doorway {
  border: 1px solid var(--line, #dcd7cc);
  border-radius: var(--radius-card, 12px);
  background: var(--surface, #fdfcfa);
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.proof-dash__doorway-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text, #1a1714);
}
.proof-dash__doorway-sub {
  font-size: 12.5px;
  color: var(--muted, #71685b);
  max-width: 46ch;
}

/* ── Resume row ─────────────────────────────────────────────────────────── */
/* Friendless CTA card (shown ONLY when hasFriends === false). A hairline card
   that replaces the passive doorway for a genuinely-friendless user: an
   invitation to bring people in, not a passive "your feed is empty". Same
   hairline shell as the doorway (1px --line, --radius-card, --surface, NO
   shadow). Prose, so it reads in the sans face — mono is DATA-only (there is no
   data). The single reserved accent on this card is the "Invite a friend"
   PRIMARY button (design.md §2 — one accent for the primary action). */
.proof-dash__friends-cta {
  border: 1px solid var(--line, #dcd7cc);
  border-radius: var(--radius-card, 12px);
  background: var(--surface, #fdfcfa);
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.proof-dash__friends-cta-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text, #1a1714);
}
.proof-dash__friends-cta-body {
  font-size: 12.5px;
  color: var(--muted, #71685b);
  max-width: 46ch;
}
.proof-dash__friends-cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.proof-dash__cta-btn {
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
  font-size: 12.5px;
  font-weight: 550;
  padding: 8px 14px;
  min-height: 36px;
  border-radius: var(--radius-btn, 8px);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 200ms ease, background 200ms ease;
}
.proof-dash__cta-btn:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
}
/* Primary — the ONE reserved accent on this card. Warm ink on the accent fill
   (--on-accent, fallback --bg — never literal #fff, design.md §4). */
.proof-dash__cta-btn--primary {
  color: var(--on-accent, var(--bg, #fdfcfa));
  background: var(--accent, #c23d12);
  border: 1px solid var(--accent, #c23d12);
}
.proof-dash__cta-btn--primary:hover {
  background: color-mix(in srgb, var(--text, #1a1714) 8%, var(--accent, #c23d12));
}
/* Secondary — a quiet hairline, NO accent (design.md §2: one accent per surface). */
.proof-dash__cta-btn--secondary {
  color: var(--text, #1a1714);
  background: transparent;
  border: 1px solid var(--line-strong, #c9c3b6);
}
.proof-dash__cta-btn--secondary:hover {
  border-color: color-mix(in srgb, var(--text, #1a1714) 20%, var(--line-strong, #c9c3b6));
}

.proof-dash__resume {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid var(--line, #dcd7cc);
  border-radius: var(--radius-card, 12px);
  padding: 11px 12px;
  cursor: pointer;
  transition: border-color 200ms ease, background 200ms ease;
  font-family: inherit;
  color: var(--text, #1a1714);
  min-height: 44px;
}
.proof-dash__resume:hover {
  border-color: var(--line-hover, #d0ccc5);
  background: var(--surface, #fdfcfa);
}
.proof-dash__resume:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
}
.proof-dash__resume-thumb {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--line, #dcd7cc);
  background: var(--surface, #fdfcfa);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  font-size: 20px;
  line-height: 1;
  user-select: none;
}
.proof-dash__resume-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.proof-dash__resume-main { flex: 1 1 0; min-width: 0; }
.proof-dash__resume-title {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text, #1a1714);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.proof-dash__resume-meta {
  display: block;
  font-family: var(--font-mono, 'Geist Mono', ui-monospace, monospace);
  font-size: 10.5px;
  color: var(--faint, #766d60);
  font-variant-numeric: tabular-nums;
  margin-top: 2px;
}
.proof-dash__resume-go {
  flex: none;
  font-size: 11.5px;
  color: var(--accent, #c23d12);
  border: 1px solid color-mix(in srgb, var(--accent, #c23d12) 40%, transparent);
  border-radius: var(--radius-btn, 8px);
  padding: 6px 11px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
}

/* ── Your-library preview grid ──────────────────────────────────────────────
   A condensed peek at the most-recently-touched recipes. Its OWN grid class —
   the real .proof-library-grid is not repurposed here. Matches the full library
   grid's column count so real titles read cleanly at every breakpoint: 1-up
   below 768 (a 2-up mobile grid truncates real titles — "Sourdo…" — at 375),
   2-up 768–1023, 3-up at ≥1024. The 5th + 6th cards are rendered but
   display:none below 1024 so the peek stays short (4 full-width cards at <768;
   two rows of 2 at 768–1023) and expands to all 6 (two rows of 3) at ≥1024. A
   display:none card drops out of tab order — no hidden reachable control. */
.proof-dash__lib-grid {
  display: grid;
  /* minmax(0, 1fr) — NOT a bare 1fr (= minmax(auto, 1fr)): a stat card's
     min-content (e.g. a "rating ★ 4.6" cell) can exceed a shrunk track, so bare
     1fr tracks refuse to shrink and the grid overflows the viewport. minmax(0…)
     lets the tracks shrink to the container; each card's internal min-width:0 +
     ellipsis handle the rest (Rule 7 — correct at 375). */
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}
.proof-dash__lib-grid > :nth-child(n + 5) {
  display: none;
}
@media (min-width: 768px) {
  .proof-dash__lib-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}
@media (min-width: 1024px) {
  .proof-dash__lib-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  }
  .proof-dash__lib-grid > :nth-child(n + 5) {
    display: block;
  }
}

/* Empty peek — a quiet doorway to the (also-empty) library. No hype, no
   fabricated cards; branding.md §5 (quiet register, no exclamations). */
.proof-dash__lib-empty {
  border: 1px solid var(--line, #dcd7cc);
  border-radius: var(--radius-card, 12px);
  background: var(--surface, #fdfcfa);
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.proof-dash__lib-empty-sub {
  font-size: 12.5px;
  color: var(--muted, #71685b);
  max-width: 46ch;
}
.proof-dash__lib-empty-link {
  font-family: var(--font-sans, 'Inter', system-ui, sans-serif);
  font-size: 12.5px;
  color: var(--muted, #71685b);
  background: transparent;
  border: 0;
  padding: 6px 2px;
  min-height: 32px;
  cursor: pointer;
  border-radius: var(--radius-btn, 8px);
}
.proof-dash__lib-empty-link:hover { text-decoration: underline; }
.proof-dash__lib-empty-link:focus-visible {
  outline: 2px solid var(--accent, #c23d12);
  outline-offset: 2px;
}

@media (max-width: 400px) {
  .proof-dash__friend { min-width: 200px; max-width: 200px; }
}
`;

if (typeof document !== "undefined") {
  const id = "proof-dashboard-hero-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = DASHBOARD_CSS;
    document.head.appendChild(el);
  }
}

// ─── Friend card (FeedItemCard + optional photo banner) ─────────────────────

/**
 * The photo banner for a friend card: the cook/feature photo, shown ONLY after
 * the image actually DECODES (onLoad). Before decode — and on a mint failure,
 * a load error, or an absent photo — the banner reserves no space and shows no
 * placeholder (photos are enrichment; a photo-less/slow cook falls back to the
 * plain composed card, never a fabricated tile). `photoMediaKey` is the media
 * UUID the feed row carries; a mint failure resolves to null → no banner (Rule
 * 8: useMediaUrl logs and degrades, never a broken-image box). The <img> is kept
 * in the DOM while the banner is collapsed (height:0) so it still loads and
 * onLoad can fire.
 */
function FriendPhotoBanner({ photoMediaKey }: { photoMediaKey: string }) {
  const url = useMediaUrl(photoMediaKey, "card-768", { ready: true, width: 216 });
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setLoaded(false);
    setBroken(false);
  }, [url]);
  if (!url || broken) return null;
  return (
    <div
      className="proof-dash__friend-banner"
      {...(loaded ? { "data-loaded": "true" } : {})}
      aria-hidden="true"
    >
      <img src={url} alt="" onLoad={() => setLoaded(true)} onError={() => setBroken(true)} />
    </div>
  );
}

function FriendCard({
  item,
  now,
  viewerHandle,
  loadNods,
  onToggleNod,
}: {
  item: FeedItem;
  now?: number;
  viewerHandle?: string;
  loadNods: (attemptUuid: string) => Promise<string[]>;
  onToggleNod: (attemptUuid: string, on: boolean) => Promise<void>;
}) {
  return (
    <div className="proof-dash__friend" data-testid="dashboard-friend-card">
      {item.photoMediaKey ? <FriendPhotoBanner photoMediaKey={item.photoMediaKey} /> : null}
      <div className="proof-dash__friend-body">
        <FeedItemCard
          item={item}
          preview
          {...(now !== undefined ? { now } : {})}
          {...(viewerHandle !== undefined ? { viewerHandle } : {})}
          loadNods={loadNods}
          onToggleNod={onToggleNod}
        />
      </div>
    </div>
  );
}

// ─── Resume row ─────────────────────────────────────────────────────────────

function ResumeRow({ recipe, onOpen }: { recipe: Recipe; onOpen: (uuid: string) => void }) {
  const url = useMediaUrl(recipe.featureMediaUuid ?? null, "thumb-256", { ready: true, width: 80 });
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const showPhoto = Boolean(recipe.featureMediaUuid) && Boolean(url) && !broken;
  const emoji = recipe.emoji ?? FALLBACK_EMOJI;
  const rel = formatShortRelative(recipeRecency(recipe));
  const meta = recipe.hasDraft === true ? `draft · ${rel}` : `edited ${rel}`;

  return (
    <button
      type="button"
      className="proof-dash__resume"
      data-testid="dashboard-resume"
      onClick={() => onOpen(recipe.uuid)}
      aria-label={`Resume ${recipe.title}`}
    >
      <span className="proof-dash__resume-thumb" aria-hidden="true">
        {showPhoto ? <img src={url ?? undefined} alt="" onError={() => setBroken(true)} /> : emoji}
      </span>
      <span className="proof-dash__resume-main">
        <span className="proof-dash__resume-title">{recipe.title}</span>
        <span className="proof-dash__resume-meta">{meta}</span>
      </span>
      <span className="proof-dash__resume-go" aria-hidden="true">
        Open
      </span>
    </button>
  );
}

// ─── DashboardHero ──────────────────────────────────────────────────────────

export interface DashboardHeroProps {
  /** The loaded library (already on the /app route). Drives status + Resume. */
  recipes: Recipe[];
  /** Signed-in display name (greeting). Falls back to a name-less greeting. */
  name?: string | null;
  /** Open the most-recently-touched recipe (parent owns go-to + Rule-8 logging). */
  onOpenRecipe: (uuid: string) => void;
  /** Navigate to the full feed (existing /app/feed route — no new backend). */
  onOpenFeed: () => void;
  /** Navigate to the full library grid (/app/library) — the "Your library"
   *  preview's "All →" link and its empty-state doorway. */
  onOpenLibrary: () => void;
  /** Open the new-recipe modal (the header's accent primary CTA). Parent owns the
   *  shared create path (name prompt, recipe.create, navigation, error handling). */
  onNewRecipe: () => void;
  /**
   * Recent friend activity. Empty on the /app route today (feed data is not
   * loaded there) → the strip shows its calm doorway. Pass real items to render
   * the photo strip.
   */
  friendItems?: FeedItem[];
  /**
   * Whether the signed-in user has at least one ACTIVE friend — a boolean-only
   * signal (never a count) that gates the "From friends" section's THIRD state,
   * the friendless-user CTA. This is a genuine "zero active friends" signal, NOT
   * `friendItems.length === 0` (which only means "no activity loaded"). Three
   * states result:
   *   • `false`               → the CTA card (invite / find friends).
   *   • `true` (no activity)  → the passive feed doorway.
   *   • `undefined` (loading) → the passive doorway too — never flash the CTA at
   *                             an active user while the signal is in flight.
   * (Loaded friend activity always wins over all three — the strip renders.)
   */
  hasFriends?: boolean | undefined;
  /** Open Settings → Friends → Invite subsection (the CTA's primary action). */
  onInviteFriend?: () => void;
  /** Open Settings → Friends → Find people subsection (the CTA's secondary action). */
  onFindFriends?: () => void;
  /** Viewer handle for nod pressed-state derivation (forwarded to FeedItemCard). */
  viewerHandle?: string;
  /**
   * Whether the recipe list has settled. While false (cold load), the status
   * counts + Resume row are withheld so a populated account never flashes a
   * false "No recipes yet" during the initial fetch (mirrors LibraryView's
   * loading ≠ empty rule). The greeting + friends doorway are load-independent.
   */
  ready?: boolean;
  // Injectable for tests (default to the real oRPC client).
  now?: number;
  loadNods?: (attemptUuid: string) => Promise<string[]>;
  onToggleNod?: (attemptUuid: string, on: boolean) => Promise<void>;
}

export function DashboardHero({
  recipes,
  name,
  onOpenRecipe,
  onOpenFeed,
  onOpenLibrary,
  onNewRecipe,
  friendItems = [],
  hasFriends,
  onInviteFriend,
  onFindFriends,
  viewerHandle,
  ready = true,
  now,
  loadNods = loadNodHandlesApi,
  onToggleNod = toggleNodApi,
}: DashboardHeroProps) {
  // The strip composes FeedItemCard, whose visual lives in the feed stylesheet.
  // FeedView injects it on mount; the dashboard must too, or a friend card
  // renders UNSTYLED on the /app landing until the user first visits /app/feed
  // (the undefined-style trap, incident 2026-07-09). Idempotent + id-guarded.
  injectFeedStyles();
  const nowMs = now ?? Date.now();
  const nowDate = new Date(nowMs);
  const status = deriveDashboardStatus(recipes);
  const resume = deriveResumeRecipe(recipes);

  // "Your library" preview — the most-recently-touched recipes (max of last-edit
  // and last-cook, the same recency the Resume row uses). Derived ENTIRELY from
  // the already-loaded `recipes` (no new fetch). Sorted DESC, top 6 taken; CSS
  // shows 4 (1-up <768, 2-up 768–1023) below 1024 and all 6 (3-up) at ≥1024. A stable tiebreak
  // (title) keeps the order deterministic when two recipes share a recency.
  const libraryPreview = [...recipes]
    .sort((a, b) => {
      const diff = recipeRecency(b) - recipeRecency(a);
      return diff !== 0 ? diff : a.title.localeCompare(b.title);
    })
    .slice(0, 6);

  const trimmedName = typeof name === "string" ? name.trim() : "";
  const greeting =
    trimmedName.length > 0
      ? `${greetingForHour(nowDate.getHours())}, ${trimmedName}`
      : greetingForHour(nowDate.getHours());
  const dateLabel = nowDate
    .toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })
    .toUpperCase();

  return (
    <section className="proof-dash" data-testid="dashboard-hero" aria-label="Dashboard">
      {/* 1 — Greeting + mono status line (derived from `recipes` only), with the
             new-recipe accent CTA on the opposite (top-right) side. */}
      <div className="proof-dash__header">
        <div className="proof-dash__header-text">
          <div className="proof-dash__greet-row">
            <h1 className="proof-dash__greet" data-testid="dashboard-greeting">
              {greeting}
            </h1>
            <span className="proof-dash__date">{dateLabel}</span>
          </div>
          <div className="proof-dash__status" data-testid="dashboard-status">
            {!ready ? null : status.recipes === 0 ? (
              <span>No recipes yet</span>
            ) : (
              <>
                <span>
                  <b>{status.recipes}</b> {status.recipes === 1 ? "recipe" : "recipes"}
                </span>
                {status.drafts > 0 && (
                  <>
                    <span className="proof-dash__sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="proof-dash__status-hot">
                      <b>{status.drafts}</b> {status.drafts === 1 ? "draft" : "drafts"}
                    </span>
                  </>
                )}
                {status.cooks > 0 && (
                  <>
                    <span className="proof-dash__sep" aria-hidden="true">
                      ·
                    </span>
                    <span>
                      <b>{status.cooks}</b> {status.cooks === 1 ? "cook" : "cooks"}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          className="proof-dash__new-recipe"
          onClick={onNewRecipe}
          data-testid="dashboard-new-recipe"
        >
          New recipe
        </button>
      </div>

      <hr className="proof-dash__hr" />

      {/* 2 — From friends: photo strip, or calm doorway when none loaded. */}
      <div className="proof-dash__seclabel">
        <span className="proof-dash__slabel">From friends</span>
        <button type="button" className="proof-dash__seclink" onClick={onOpenFeed}>
          Feed →
        </button>
      </div>
      {/* Three states, in priority order:
          1. Loaded friend activity → the photo strip (always wins).
          2. hasFriends === false (a GENUINE zero-active-friends signal, not an
             empty activity load) → the friendless-user CTA card.
          3. otherwise (hasFriends true with no activity, OR the signal still in
             flight / unknown) → the passive doorway. Falling back to the doorway
             while `hasFriends` is undefined is deliberate: never flash the CTA at
             an active user before the signal resolves. */}
      {friendItems.length > 0 ? (
        <div className="proof-dash__strip" data-testid="dashboard-friends-strip">
          {friendItems.map((item) => (
            <FriendCard
              key={item.id}
              item={item}
              {...(now !== undefined ? { now } : {})}
              {...(viewerHandle !== undefined ? { viewerHandle } : {})}
              loadNods={loadNods}
              onToggleNod={onToggleNod}
            />
          ))}
        </div>
      ) : hasFriends === false ? (
        <div className="proof-dash__friends-cta" data-testid="dashboard-friends-cta">
          <span className="proof-dash__friends-cta-title">Cook alongside friends</span>
          <span className="proof-dash__friends-cta-body">
            Invite someone you cook with, or find people you already know.
          </span>
          <div className="proof-dash__friends-cta-actions">
            <button
              type="button"
              className="proof-dash__cta-btn proof-dash__cta-btn--primary"
              data-testid="dashboard-friends-cta-invite"
              onClick={() => {
                // Rule 8: navigation, but never a silent swallow if the opener throws.
                try {
                  onInviteFriend?.();
                } catch (err) {
                  console.error({ event: "dashboard_friends_cta_invite_failed", err });
                }
              }}
            >
              Invite a friend
            </button>
            <button
              type="button"
              className="proof-dash__cta-btn proof-dash__cta-btn--secondary"
              data-testid="dashboard-friends-cta-find"
              onClick={() => {
                try {
                  onFindFriends?.();
                } catch (err) {
                  console.error({ event: "dashboard_friends_cta_find_failed", err });
                }
              }}
            >
              Find friends
            </button>
          </div>
        </div>
      ) : (
        <div
          className="proof-dash__doorway"
          data-testid="dashboard-friends-strip"
          data-empty="true"
        >
          <span className="proof-dash__doorway-title">Recent cooks from friends</span>
          <span className="proof-dash__doorway-sub">
            Cooks your friends share show up in your feed.
          </span>
        </div>
      )}

      {/* 3 — Resume the most-recently-touched recipe (omitted for an empty
          library — a brand-new user simply has no Resume row). */}
      {ready && resume !== null && (
        <>
          <hr className="proof-dash__hr" />
          <div className="proof-dash__seclabel">
            <span className="proof-dash__slabel">Resume</span>
          </div>
          <ResumeRow recipe={resume} onOpen={onOpenRecipe} />
        </>
      )}

      {/* 4 — Your library: a condensed peek at the most-recently-touched recipes,
          with an "All →" link to the full grid (/app/library). The full library
          lives on its own surface now (dashboard/library split, 2026-07-11); this
          preview is derived from the already-loaded `recipes` (no new fetch).
          Withheld until the list settles (mirrors Resume) so a populated account
          never flashes a false empty peek during the cold fetch. */}
      {ready && (
        <>
          <hr className="proof-dash__hr" />
          <div className="proof-dash__seclabel">
            <span className="proof-dash__slabel">Your library</span>
            {/* "All →" is a section nav link, NOT a primary CTA — muted, with the
                reserved accent only on :focus-visible (the Resume "Open" stays the
                single persistent accent on-screen). design.md §2. */}
            <button
              type="button"
              className="proof-dash__seclink"
              onClick={onOpenLibrary}
              data-testid="dashboard-library-all"
            >
              All →
            </button>
          </div>
          {libraryPreview.length > 0 ? (
            <div className="proof-dash__lib-grid" data-testid="dashboard-your-library">
              {libraryPreview.map((recipe) => (
                <RecipeCard
                  key={recipe.uuid}
                  recipe={recipe}
                  isActive={false}
                  onClick={onOpenRecipe}
                />
              ))}
            </div>
          ) : (
            <div
              className="proof-dash__lib-empty"
              data-testid="dashboard-your-library"
              data-empty="true"
            >
              <span className="proof-dash__lib-empty-sub">Recipes you create show up here.</span>
              <button
                type="button"
                className="proof-dash__lib-empty-link"
                onClick={onOpenLibrary}
                data-testid="dashboard-library-empty-link"
              >
                Open the library →
              </button>
            </div>
          )}
        </>
      )}

      <hr className="proof-dash__hr" />
    </section>
  );
}
